/**
 * 腾讯广告 → 授权更新（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；本页复用 leo-* 类，
 * 适用章节：§2.3 颜色规则 / §3.2 字号阶梯 / §4.2 布局模式 A / §6.1 按钮层级 /
 * §6.4 卡片 / §6.7 Toast / §7.2 反馈原则）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/tencent/user-token/qrcode 拉取授权链接 + 二维码。
 *   - 二维码以 data:image/png;base64, 内联渲染（白底容器保证扫码对比度）。
 *   - 「复制链接」复制授权 URL 到剪贴板（leo-btn-ghost 次级按钮）。
 *   - 「刷新二维码」重新拉取（本页唯一主 CTA，leo-cta-primary 橙渐变；
 *     其 CSS 为 width:100%，故用 w-44 定宽容器包裹）。
 *   - 异常：redirect_uri 未配置（后端 400）时 Toast + 页内警告卡展示 message
 *     与 suggestion（指引配置 config.json#platforms.tx_user_token_redirect_uri）。
 *
 * 本页只读（无写操作），任意登录用户可访问，无独立权限键。
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type UserTokenAuthQr,
  getUserTokenAuthQr,
} from "../../services/tencent/userTokenAuth";
import { pushToast } from "../../hooks/useToast";

/** 授权流程步骤说明（腾讯平台通用组件扫码实名认证）。 */
const AUTH_STEPS = [
  "使用微信扫描左侧二维码，进入腾讯广告平台通用组件",
  "按页面指引完成实名认证（人脸核验）",
  "认证完成后腾讯回调 redirect_uri，user_token 自动更新",
];

export default function UserTokenAuthPage() {
  const [qr, setQr] = useState<UserTokenAuthQr | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; suggestion?: string } | null>(
    null,
  );

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserTokenAuthQr();
      setQr(data);
      if (refresh) pushToast("二维码已刷新", "success");
    } catch (err) {
      setQr(null);
      const message =
        err instanceof ApiError ? err.message : "生成授权二维码失败";
      const suggestion =
        err instanceof ApiError ? err.suggestion : undefined;
      setError({ message, suggestion });
      pushToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const copyLink = useCallback(async () => {
    if (!qr) return;
    try {
      await navigator.clipboard.writeText(qr.authorize_url);
      pushToast("授权链接已复制", "success");
    } catch {
      pushToast("复制失败，请手动选择链接复制", "error");
    }
  }, [qr]);

  if (loading && !qr && !error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
            授权更新
          </h1>
          <p className="mt-1 text-[12.5px] text-text-muted">
            腾讯 Marketing API 操作类接口（如广告启停）所需 user_token
            需定期扫码实名认证更新；微信扫码完成认证后自动生效。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="leo-tag is-info">微信扫码 · 实名认证</span>
        </div>
      </div>

      {error ? (
        <div className="leo-card rounded-xl p-8 text-center">
          <span className="leo-tag is-warning">配置缺失</span>
          <div className="mt-3 text-[12.5px] text-text-muted">{error.message}</div>
          {error.suggestion ? (
            <div className="mt-2 font-mono text-[11px] text-text-muted-weak">
              {error.suggestion}
            </div>
          ) : null}
        </div>
      ) : qr ? (
        <div className="rounded-xl border border-line-strong bg-da-card p-5 shadow-card md:p-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            {/* 二维码：白底容器保证扫码对比度（后端 PNG 为黑白码） */}
            <div className="shrink-0 self-center md:self-start">
              <div className="rounded-xl bg-white p-3">
                <img
                  src={`data:image/png;base64,${qr.qr_png_base64}`}
                  alt="腾讯 USER_TOKEN 授权二维码"
                  className="h-[220px] w-[220px]"
                />
              </div>
              <div className="mt-2 text-center text-[11px] text-text-muted-weak">
                微信扫码进入实名认证
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-[15px] font-bold text-text-emphasis">操作步骤</h2>
              <ol className="mt-2 space-y-1.5">
                {AUTH_STEPS.map((step, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-[12.5px] text-text-body"
                  >
                    <span className="leo-tag is-info mt-0.5 shrink-0">{idx + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>

              <h2 className="mt-5 text-[15px] font-bold text-text-emphasis">
                授权链接
              </h2>
              <div className="mt-2 break-all rounded-md border border-line-subtle bg-black/25 p-3 font-mono text-[12px] leading-relaxed text-text-muted">
                {qr.authorize_url}
              </div>

              <div className="mt-4 flex items-center gap-3">
                {/* leo-cta-primary CSS 为 width:100%，用定宽容器约束（§6.1 每区块唯一主 CTA） */}
                <div className="w-44">
                  <button
                    type="button"
                    className="leo-cta-primary"
                    disabled={loading}
                    onClick={() => void load(true)}
                  >
                    {loading ? "刷新中…" : "刷新二维码"}
                  </button>
                </div>
                <button
                  type="button"
                  className="leo-btn-ghost"
                  onClick={() => void copyLink()}
                >
                  复制链接
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-3 text-[11px] leading-relaxed text-text-muted-weak">
        回调地址由服务端 config.json#platforms.tx_user_token_redirect_uri
        配置，需与腾讯侧登记地址一致；本页仅展示授权入口，不保存任何扫码状态。
      </div>
    </div>
  );
}
